import React, { useEffect } from 'react';
import { motion, useAnimate, inView } from 'framer-motion';

import SectionColumns from '../../SectionBuilder/SectionColumns/SectionColumns';
import { MotionSection } from '../scrollAnimations';

import {
  EASE_OUT,
  slideInFromLeft,
  fadeUpHeading,
  fadeUpListItem,
  fadeUpCta,
  listItemStagger,
} from './RewardYouAnimations';

/**
 * Reward You — two-column section where the right column holds a heading,
 * a markdown `<ul>` with 4 list items, and a CTA.
 *
 * Console sectionId: `reward-you-section` (columns, 2 blocks).
 *   - Block 1: left photo.
 *   - Block 2: title (`…let it **Reward you**`), markdown list (icons + copy),
 *     and "How lending works" CTA.
 *
 * Animation:
 *  - Left photo slides in from the left.
 *  - Right heading rises from below.
 *  - The 4 list items fade up one after another (stagger).
 *  - CTA follows with a gentle rise.
 *
 * Uses `useAnimate` + `inView` on existing DOM nodes (no wrapper divs), so the
 * block-level CSS in SectionContainer.module.css (`#reward-you-section-*`)
 * stays intact.
 *
 * @component
 * @param {Object} props Page Builder section props
 * @returns {JSX.Element}
 */
const RewardYouSection = props => {
  const { sectionId = 'reward-you-section', options = {} } = props;

  const [scope, animate] = useAnimate();

  // Per-element reveal: photo from left, heading + list items + CTA from below.
  useEffect(() => {
    if (!scope.current) return;
    const root = scope.current;

    const cleanup = inView(
      scope.current,
      () => {
        // Left photo column
        const mediaBlock = root.querySelector(`#${sectionId}-block-1`);
        if (mediaBlock) {
          animate(mediaBlock, slideInFromLeft, { duration: 0.85, ease: EASE_OUT });
        }

        // Right column: heading
        const heading = root.querySelector(`#${sectionId}-block-2 h2`);
        if (heading) {
          animate(heading, fadeUpHeading, { duration: 0.7, ease: EASE_OUT });
        }

        // Right column: the 4 list items (icons + copy)
        const listItems = Array.from(root.querySelectorAll(`#${sectionId}-block-2 li`));
        if (listItems.length) {
          animate(listItems, fadeUpListItem, {
            delay: listItemStagger,
            duration: 0.6,
            ease: EASE_OUT,
          });
        }

        // Right column: CTA link
        const cta = root.querySelector(`#${sectionId}-block-2 p:has(a)`);
        if (cta) {
          animate(cta, fadeUpCta, {
            delay: 0.5,
            duration: 0.6,
            ease: EASE_OUT,
          });
        }
      },
      { amount: 0.12 }
    );

    return cleanup;
    // animate/scope are stable references from useAnimate; sectionId never changes at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  return (
    <MotionSection>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.08 }}
        transition={{ duration: 0.75, ease: EASE_OUT }}
      >
        <div ref={scope}>
          <SectionColumns {...props} sectionId={sectionId} numColumns={2} options={options} />
        </div>
      </motion.div>
    </MotionSection>
  );
};

export default RewardYouSection;